import {useSearchParams} from "react-router-dom";
import React, {useEffect, useState} from "react";
import {Condition, ruleCategoriesEnum, ruleMetadata, TimeParam, TRIGGER_ACTION} from "../../types/ruleTypes";
import {createRule, getRuleById, getRulesMetadata} from "../../queries/ruleQueries";
import {enqueueSnackbar} from "notistack";
import Box from "@mui/material/Box";
import CustomAutoComplete from "../custom-components/CommonComponents/CustomAutoComplete";
import {
    CURRENT_SPORT,
    defaultContentSpecificParams,
    SUPPORTED_CONTENTS,
    SUPPORTED_SPORTS,
    whitelistedPlaceholders,
} from "../../constants/globalConstants";
import {RuleConditionForm} from "../custom-components/RuleComponents/RuleConditionForm";
import TextField from "@mui/material/TextField";
import ContentAggregation, {AGGREGATION_USAGE_TYPE} from "../extra-pages/ContentAggregation";
import {useSelectedCard} from "../../contexts/SelectedCardContext";
import RuleSave from "../custom-components/RuleComponents/RuleActionParameter/RuleSave";
import RuleRender from "../custom-components/RuleComponents/RuleActionParameter/RuleRender";
import Typography from "@mui/material/Typography";
import {useContentSpecificParams} from "../../contexts/ContentSpecificParamsContext";
import {Button, CircularProgress, Grid, Stack} from "@mui/material";
import {useMaterializationSettings} from "../../contexts/MaterializationSettingsContext";
import {useEditorialAssets} from "../../contexts/EditorialAssetsContext";
import {updateRule} from "queries/ruleQueries";
import {EditorialAsset} from "../../types/editorialAssetsTypes";
import {formatUTCDate} from "../../utils/utils";
import {getCompetitionOptions} from "../../queries/competitionQueries";
import {getListOptions} from "../../queries/utils";
import DividerLabel from "../custom-components/CommonComponents/DividerLabel";
import SocialPublish from "@/pages/custom-components/SocialPublish/SocialPublish";
import {CheckBox} from "@mui/icons-material";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import {defaultLanguages} from "@/contexts/SocialPublicationContext";
import { defaultStyles, useSocialPublication } from "contexts/SocialPublicationContext";
import {useIntl} from "react-intl";


const renderActionContent = (action: TRIGGER_ACTION, clipName: string, setClipName: (val: string) => void) => {
    switch (action) {
        case TRIGGER_ACTION.CREATE_LOGIC_CLIP:
            return <RuleSave clipName={clipName} onClipNameChange={setClipName} />;
        case TRIGGER_ACTION.CREATE_MATERIALIZE_LOGIC_CLIP:
            return <RuleRender clipName={clipName} onClipNameChange={setClipName} />;
        default:
            return null;
    }
};

enum RULE_FORM_MODE {
    CREATE = 'create',
    UPDATE = 'update'
}

const RuleForgePage = () => {

    const intl = useIntl();

    const [loading, setLoading] = useState<boolean>(false);

    const [searchParams, setSearchParams] = useSearchParams();
    const { contentSpecificParams, setContentSpecificParams} = useContentSpecificParams()
    const {fade, fadeDuration, aspectRatio, audioTrack, setFade, setFadeDuration, setAudioTrack, setAspectRatio} = useMaterializationSettings()
    const {
        assetRenderParametersMap,
        loadEditorialAssets,
        selectedEditorialAssets,
        setSelectedEditorialAssets,
        editorialAssets
    } = useEditorialAssets();

    const { updatePlatformState, socialOptions, loadSocialOptions } = useSocialPublication();

    const { resetCardContext } = useSelectedCard()
    const { resetEditorialAssetSelection } = useEditorialAssets()

    const [mode, setMode] = useState<RULE_FORM_MODE>(RULE_FORM_MODE.CREATE);
    const [ruleId, setRuleId] = useState<string|null>(null);
    const [ruleCategoryOptions, setRuleCategoryOptions] = useState<string[]>([])
    const [ruleCategory, setRuleCategory] = useState<string | null>(null);
    const [rule, setRule] = useState<string | null>(null);
    const [schemaTriggers, setSchemaTriggers] = useState<Condition[]>([]);
    const [contentTypeOptions, setContentTypeOptions] = useState<string[]>([]);
    const [ruleName, setRuleName] = useState<string>('');
    const [action, setAction] = useState<string | null>(null);
    const [triggerValues, setTriggerValues] = useState<any>({}); // TRIGGER VALUES È PER ACTIVATION FILTERS VALUE
    const [filterValues, setFilterValues] = useState<any>({}) // FILTER VALUES È PER FILTER VALUES
    const [timeParams, setTimeParams] = useState<TimeParam[]>([]);
    const [timeParamsValues, setTimeParamsValues] = useState<any>({});
    const [clipName, setClipName] = useState<string>('');
    const [scheduler, setScheduler] = useState<string>('');
    const [ruleMetadataUpdated, setRuleMetadataUpdated] = useState<any>(ruleMetadata)
    const [schemaFilters, setSchemaFilters] = useState<Condition[]>([]);
    const [resourceFilters, setResourceFilters] = useState<any>({})
    const [isSingleClip, setIsSingleClip] = useState<boolean>(false);
    const [ruleSocialInfo, setRuleSocialInfo] = useState<any>(null);
    const [errorPlaceholder, setErrorPlaceholder] = useState<boolean>(false);
    const [ruleDescription, setRuleDescription] = useState<string>('');

    const [creationSettings, setCreationSettings] = useState<any>({})

    const excludedArraysForSingleClip = ['on_event_analyzed']

    const { contentType, setContentType } = useSelectedCard()

    const validateClipNamePlaceholders = () => {
        if (!clipName) {
            setErrorPlaceholder(false);
            return;
        }

        const regex = /\[([^\]]+)]/g;
        let match;
        let hasError = false;

        while ((match = regex.exec(clipName)) !== null) {
            const innerValue = match[1];
            if (!whitelistedPlaceholders.includes(innerValue)) {
                hasError = true;
                break; // Interrompiamo al primo errore trovato
            }
        }

        // Aggiorniamo lo stato in base al risultato dell'intero controllo
        setErrorPlaceholder(hasError);
        if (hasError) {
            enqueueSnackbar('Clip name placeholders not valid');
        }
    };

    const creationSettingsCreator = () => {
        const getProductionSettings = () => {
            const requestEditorialAssets: Record<string, [{id: string, params: any}]> =  {}
            for (const editorialAsset of selectedEditorialAssets){
                requestEditorialAssets[editorialAsset.placement_type] = [{
                    id: editorialAsset.id,
                    params: {}
                }]
            }
            return {
                fade,
                fade_duration: fadeDuration,
                content_type: contentType,
                aspect_ratio: parseInt(aspectRatio.split(":")[0])/parseInt(aspectRatio.split(":")[1]),
                ...requestEditorialAssets
            }
        }
        const getMatchHighlightSuggestSettings = () => {
            return {
                min_duration: contentSpecificParams.minDuration,
                max_duration: contentSpecificParams.maxDuration
            }
        }
        const getSuggestSettings = () => {
            switch (contentType){
                case SUPPORTED_CONTENTS[CURRENT_SPORT as keyof typeof SUPPORTED_SPORTS].MATCH_HIGHLIGHT: return getMatchHighlightSuggestSettings()
                default: return {}
            }
        }
        function getRequestCreationSettings(productionSettings : any, suggestSettings: any){
            return {
                ...productionSettings,
                suggest_params: suggestSettings
            }
        }

        const productionSettings = getProductionSettings()
        const suggestSettings = getSuggestSettings()
        const creation_settings = getRequestCreationSettings(productionSettings, suggestSettings);
        return creation_settings;
    }

    // Solo per le query di post e put (perchè serve solo l'id) NO PER GLI SCHEMI
    const formatConditionsForQueries = (uiConditions: any) => {
        const apiValues = { ...uiConditions };
        const arrayFields = ['teams', 'competitions', 'events', "periods", "matchdays"];

        arrayFields.forEach((field) => {
            if (Array.isArray(apiValues[field]) && apiValues[field].length > 0) {
                apiValues[field] = apiValues[field].map((item: any) => item.id);
            } else {
                apiValues[field] = undefined;
            }
        });

        return apiValues;
    };

    const handleAddId = (newId: string) => {
        const currentParams = new URLSearchParams(searchParams);
        currentParams.set('id', newId);
        setSearchParams(currentParams);
    };

    //UTILE SOLO PER LA VISUALIZZAZIONE E MAPPING PER GLI SCHEMI DEL FORM

    const mapFilterValuesForDisplay = (filters: any, currentResources: any) => {
        const filtersForQueries = {...filters}

        if(filtersForQueries?.competitionFilterValues) {
            filtersForQueries.competitions = filtersForQueries.competitionFilterValues;
            delete filtersForQueries.competitionFilterValues;
        }
        if(filters?.matchFilterValues) {
            filtersForQueries.events = filtersForQueries.matchFilterValues;
            delete filtersForQueries.matchFilterValues;
        }
        if(filters?.keyMomentTypeValues) {
            filtersForQueries.key_moment_types = filtersForQueries.keyMomentTypeValues;
            delete filtersForQueries.keyMomentTypeValues;
        }
        if(filters?.matchDayFilterValues) {
            filtersForQueries.matchdays = filtersForQueries.matchDayFilterValues
            delete filtersForQueries.matchDayFilterValues
        }
        if(filters?.periodFilterValues) {
            filtersForQueries.periods = filtersForQueries.periodFilterValues
            delete filtersForQueries.periodFilterValues
        }
        if(filters?.teamFilterValues) {
            filtersForQueries.teams = filtersForQueries.teamFilterValues
            delete filtersForQueries.teamFilterValues
        }

        const arrayFields = ['teams', 'competitions', 'events', 'key_moment_types', 'periods', 'matchdays'];

        arrayFields.forEach((field) => {
            if (Array.isArray(filtersForQueries[field])) {
                filtersForQueries[field] = filtersForQueries[field].map((item: any) => {
                    let displayValue = item.id;

                    switch (field) {
                        case 'competitions':
                            displayValue = `${currentResources?.competitions?.[item.id]?.name || ''} - ${currentResources?.competitions?.[item.id]?.season || ''}`;
                            break;
                        case 'teams':
                            displayValue = `${currentResources?.teams?.[item.id]?.name || ''}`;
                            break;
                        case 'events':
                            displayValue = `${currentResources?.events?.[item.id]?.name || ''} / ${formatUTCDate(currentResources?.events?.[item.id]?.start_schedule, false, false)}`;
                            break;
                        case 'periods':
                            displayValue = intl.formatMessage({id: item.id, defaultMessage: item.id});
                            break;
                        case 'key_moment_types':
                            displayValue = intl.formatMessage({id: item.id, defaultMessage: item.id});
                            break;
                        case 'matchdays':
                            displayValue = `${currentResources?.matchdays?.[item.id]?.name || ''}`;
                            break;
                        default:
                            displayValue = item.value || item.name || item.id;
                    }

                    if (field === 'periods' || field === 'key_moment_types') {
                        return {
                            id: item.id || item,
                            value: displayValue
                        }
                    }

                    return {
                        id: item.id,
                        value: displayValue
                    };
                });
            }
        });

        return filtersForQueries;
    }

    const mapTriggerValuesForDisplay = (values: any) => {
        const displayValues = { ...values };
        const arrayFields = ['teams', 'competitions', 'events', 'key_moment_types', 'periods', 'matchdays'];

        arrayFields.forEach((field) => {
            if (Array.isArray(displayValues[field])) {
                displayValues[field] = displayValues[field].map((item: any) => {
                    // Se l'elemento è già una stringa o un numero (array di primitivi come periods/key_moment_types)
                    if (typeof item !== 'object' || item === null) {
                        return {
                            id: item,
                            value: intl.formatMessage({id:item, defaultMessage: item})
                        };
                    }

                    // Se l'elemento è un oggetto complesso, mappiamo in base al field specifico
                    let computedValue = item.name || item.id || '';

                    switch (field) {
                        case 'competitions':
                            computedValue = `${item.name || ''} ${item.season ? `- ${item.season}` : ''}`.trim() || item.id;
                            break;
                        case 'events':
                            computedValue = item.start_schedule
                                ? `${item.name || ''} / ${formatUTCDate(item.start_schedule, false, false)}`
                                : (item.name || item.id);
                            break;
                        case 'teams':
                            computedValue = item.name || item.id;
                            break;
                        case 'matchdays':
                            computedValue = item.name || item.id;
                            break;
                        default:
                            // Default sicuro per oggetti complessi generici
                            computedValue = item.name || item.value || item.id;
                            break;
                    }

                    return {
                        id: item.id,
                        value: computedValue
                    };
                });
            }
        });

        return displayValues;
    };

    useEffect(() => {
        const urlRuleId = searchParams.get("id");
        const currentMode = urlRuleId ? RULE_FORM_MODE.UPDATE : RULE_FORM_MODE.CREATE;
        setMode(currentMode);
        setRuleId(urlRuleId);

        const fetchAndInitialize = async () => {
            setLoading(true);
            try {
                const constraints = await getRulesMetadata();
                const compOptions = await getCompetitionOptions();
                const responseListOptions = await getListOptions(false);

                const newMetadata = { ...ruleMetadata };
                newMetadata.categories = constraints.categories;
                newMetadata.triggers = constraints.triggers;
                newMetadata.activation_filters.competitions.options = compOptions;
                newMetadata.activation_filters.key_moment_types.options = responseListOptions.action_types;
                newMetadata.activation_filters.periods.options = responseListOptions.periods;
                newMetadata.actions = constraints.actions;

                setRuleMetadataUpdated(newMetadata);
                setSchemaFilters(Object.entries(newMetadata.activation_filters).map(([key, value]) => ({
                    ...(value as object),
                    name: key
                })));

                if (currentMode === RULE_FORM_MODE.UPDATE && urlRuleId) {
                    const [rule] = await Promise.all([getRuleById(urlRuleId)]);
                    const creationSettingsParams = rule?.parameters?.create?.creation_settings || {};
                    const { id, fade, fade_duration, content_type, aspect_ratio, pix_fmt, suggest_params, ...editorialAssetSettings } = creationSettingsParams;

                    setCreationSettings(creationSettingsParams);



                    setRuleCategoryOptions(Object.keys(newMetadata.categories));
                    setContentTypeOptions(Object.keys(SUPPORTED_CONTENTS[CURRENT_SPORT as keyof typeof SUPPORTED_SPORTS]));
                    setRuleName(rule.name);
                    if(rule?.parameters?.suggest === null || rule?.parameters?.suggest === undefined) {
                        setIsSingleClip(true)
                    }
                    setRuleDescription(rule.description)
                    setRuleCategory(rule.category);
                    setRule(rule.trigger);
                    setScheduler(newMetadata.categories.time?.includes(rule.trigger) ? 'time-based' : 'trigger-based');
                    setContentType(content_type);
                    setContentSpecificParams(defaultContentSpecificParams[CURRENT_SPORT as keyof typeof SUPPORTED_SPORTS][content_type]);

                    const extractedResources = rule?.parameters?.suggest?.filter_by?.resources || {};
                    setResourceFilters(extractedResources);

                    const hydratedConditions = mapTriggerValuesForDisplay(rule.activation_filters);
                    setTriggerValues(hydratedConditions);

                    const hydratedFilters = mapFilterValuesForDisplay(rule.parameters_filters, extractedResources);
                    setFilterValues(hydratedFilters);

                    setAction(rule?.action);
                    setClipName(rule?.parameters?.create?.name || '');

                    setTimeParamsValues(mapTimeParamsValuesToDisplay(rule?.action === TRIGGER_ACTION.CREATE_MATERIALIZE_PUBLISH_LOGIC_CLIP ? {delay : rule?.parameters.publish_delay} : rule?.time_params))

                    if(rule?.parameters?.publish?.publish) {

                        const currentSocialOptions = await loadSocialOptions()

                        const socialInfo = rule?.parameters?.publish?.publish
                        const draftLang = defaultLanguages.find(l => l.key === (socialInfo as any)?.content_language);
                        const draftStyle = defaultStyles.find(s => s.key === (socialInfo as any)?.content_style);
                        const socialAccount = currentSocialOptions?.socialAccounts?.find(sa => sa.id === socialInfo.social_account_id)


                        if (socialAccount) {

                            const availablePostTypes = currentSocialOptions?.socialPostTypes.filter((pt: any) => pt.socialPlatforms.includes(socialAccount?.socialPlatform))
                            updatePlatformState(socialAccount.socialPlatform, {
                                socialAccount,
                                socialPostType: availablePostTypes ? availablePostTypes.find(pt => pt.id === socialInfo.social_content_id) : null,
                                selectedLanguage: draftLang,
                                selectedStyle: draftStyle,
                                socialFormState: {
                                    private: socialInfo?.private || false,
                                    is_created_for_kids: socialInfo?.is_created_for_kids || false,
                                    channel_id: socialInfo.channel_id || ''
                                }
                            });
                        }
                        else {
                            console.error('Social Platform not found for account', socialInfo.social_account_id);
                        }

                    }

                    // Caricamento Assets
                    let currentEditorialAssets: EditorialAsset[] = [];
                    if(hydratedConditions?.competitions?.length === 1) {
                        currentEditorialAssets = await loadEditorialAssets(content_type, hydratedConditions.competitions[0].id);
                    } else {
                        currentEditorialAssets = await loadEditorialAssets(content_type, null);
                    }

                    const tempSelectedEditorialAssets = [];
                    for (const type in editorialAssetSettings) {
                        const editorialAssetSetting = editorialAssetSettings[type];
                        if (editorialAssetSetting){
                            for (const graphicOverlay of editorialAssetSetting) {
                                const targetEditorialAsset = currentEditorialAssets.find(e => e.id === graphicOverlay.id);
                                if (targetEditorialAsset) {
                                    tempSelectedEditorialAssets.push(targetEditorialAsset);
                                }
                            }
                        }
                    }
                    setSelectedEditorialAssets(tempSelectedEditorialAssets);

                } else {
                    // MODALITÀ CREATE
                    setScheduler(newMetadata.categories?.time?.includes(rule) ? 'time-based' : 'trigger-based');
                    setRuleCategoryOptions(Object.keys(newMetadata.categories));
                    setContentTypeOptions(Object.keys(SUPPORTED_CONTENTS[CURRENT_SPORT as keyof typeof SUPPORTED_SPORTS]));
                    setContentType("CLIP");
                    loadEditorialAssets(contentType, null);
                }
            } catch (e) {
                console.error("Errore durante il caricamento dei dati:", e);
                enqueueSnackbar("Error loading rule metadata");
            }finally {
                setLoading(false);
            }
        };

        fetchAndInitialize();
    }, [searchParams]);


    const onRuleCategoryChange = (category: string) =>{
        setRuleCategory(category)
        if(category && category === 'time') {
            setScheduler('time-based')
        }
        else {
            setScheduler('trigger-based')
        }
        setIsSingleClip(false)
        setRule(null)
        setSchemaTriggers([])
    }

    const onRuleChange = (rule: string) => {
        if(excludedArraysForSingleClip.includes(rule)) {
            setIsSingleClip(false)
        }
        setRule(rule);
    };

    const onContentTypeChange = (contentType: string) => {
        setContentType(contentType)
        setContentSpecificParams(defaultContentSpecificParams[CURRENT_SPORT][contentType]);
        if(contentType !== SUPPORTED_CONTENTS[CURRENT_SPORT as keyof typeof SUPPORTED_SPORTS].CLIP){
            setIsSingleClip(false)
        }
        let targetCompetition = null
        if (triggerValues.competions && triggerValues.competitions.length === 1) {
            targetCompetition = triggerValues.competitions[0].id
        }
        loadEditorialAssets(contentType, targetCompetition)
    }

    const onConditionFilterChange = (conditionName: string, value : any) => {
        setFilterValues((prev: any) => ({...prev, [conditionName]: value}))
    }

    const mapTimeParamsValuesToDisplay = (timeparamsvalues : any) => {
        let timeParamsValuess = { ...timeparamsvalues };

        if (timeParamsValuess.start) {
            timeParamsValuess.start = new Date(timeParamsValuess.start);
        }

        return timeParamsValuess;
    }

    // Dalla creazione / update quindi dai triggerValues al backend
    const formatParametersFilters = (filters: any) => {

        const filtersForQueries = {...filters}

        if(filters?.competitions) {
            filtersForQueries.competitionFilterValues = filtersForQueries.competitions;
            delete filtersForQueries.competitions;
        }
        if(filters?.events) {
            filtersForQueries.matchFilterValues = filtersForQueries.events;
            delete filtersForQueries.events;
        }
        if(filters?.key_moment_types) {
            filtersForQueries.keyMomentTypeValues = filtersForQueries.key_moment_types;
            delete filtersForQueries.key_moment_types;
        }
        if(filters?.matchdays) {
            filtersForQueries.matchDayFilterValues = filtersForQueries.matchdays
            delete filtersForQueries.matchdays
        }
        if(filters?.periods) {
            filtersForQueries.periodFilterValues = filtersForQueries.periods
            delete filtersForQueries.periods
        }
        if(filters?.teams) {
            filtersForQueries.teamFilterValues = filtersForQueries.teams
            delete filtersForQueries.teams
        }
        return filtersForQueries
    };

    const onConditionChange = (conditionName: string, value: any) => {
        setContentSpecificParams((prev: any) => {
            if (!prev) return prev;
            if (conditionName === 'competitions') {
                loadEditorialAssets(contentType, value.length === 1 ? value[0].id : undefined)
                return {
                    ...prev,
                    [conditionName]: value,
                };
            }else{
                return {
                    ...prev,
                    [conditionName]: value,
                };
            }
        });
        setTriggerValues((prev: any) => ({...prev, [conditionName]: value}))
    }

    const onConditionChangeTimeParams = (conditionName: string, value: any) => {
        setContentSpecificParams((prev: any) => {
            if (!prev) return prev;
            if (conditionName === 'competition') {
                return {
                    ...prev,
                    [conditionName]: value,
                };
            }else{
                return {
                    ...prev,
                    [conditionName]: value,
                };
            }
        });
        setTimeParamsValues((prev: any) => ({...prev, [conditionName]: value}))
    }

    useEffect(() => {
        if (!rule) {
            setSchemaTriggers([]);
            return;
        }

        const conditionKeys = ruleMetadataUpdated?.triggers[rule] ? ruleMetadataUpdated?.triggers[rule].conditions : [];

        let newConditionKeys = []

        if(conditionKeys.includes('competitions')) {
            newConditionKeys.push("competitions")
        }
        if(conditionKeys.includes('matchdays')) {
            newConditionKeys.push("matchdays")
        }
        if(conditionKeys.includes('events')) {
            newConditionKeys.push("events")
        }
        if(conditionKeys.includes('teams')) {
            newConditionKeys.push("teams")
        }
        if(conditionKeys.includes('key_moment_types')) {
            newConditionKeys.push("key_moment_types")
        }
        if(conditionKeys.includes('periods')) {
            newConditionKeys.push("periods")
        }

        const conditions: Condition[] = [];

        if (!['periodic', 'scheduled'].includes(rule)) {
            for (const conditionKey of newConditionKeys) {
                if (ruleMetadataUpdated?.activation_filters[conditionKey]) {
                    conditions.push({ ...ruleMetadataUpdated?.activation_filters[conditionKey], name: conditionKey });
                }
            }
        } else {
            const currentRuleParams = ruleMetadataUpdated?.time_params[rule];
            for (const timeParamKey in currentRuleParams) {
                if (timeParamKey !== 'supported_actions') {
                    conditions.push({ ...currentRuleParams[timeParamKey], name: timeParamKey });
                }
            }
        }
        setSchemaTriggers(conditions);
    }, [rule]);

    useEffect(() => {
        if (ruleMetadataUpdated && rule && ruleMetadataUpdated?.time_params && ruleMetadataUpdated?.time_params[rule]) {

            const currentRuleTimeParams = ruleMetadataUpdated?.time_params[rule];

            let timeparams = [];

            for (const [key, value] of Object.entries(currentRuleTimeParams)) {
                const fieldData: any = value;
                let mappedType = fieldData.type;

                if(mappedType !== undefined ) {
                    timeparams.push({
                        ...fieldData,
                        name: key,
                        type: mappedType,
                        source: fieldData.source || 'local',
                        options: fieldData.options || []
                    });
                }
            }
            setTimeParams(timeparams);
        } else {
            setTimeParams([]);
        }
    }, [ruleMetadataUpdated, rule, triggerValues]);

    useEffect(() => {
        resetCardContext()
        resetEditorialAssetSelection()
    }, []);

    const translatedRuleCategoryOptions = ruleCategoryOptions.map(opt =>
    ({id: opt, value: intl.formatMessage({ id: opt, defaultMessage: opt })})
    );

    const translatedContentTypeOptions = contentTypeOptions.map(opt =>
        ({id: opt, value: intl.formatMessage({ id: opt, defaultMessage: opt })})
    );

    const triggerTypeOptions = ruleCategory && ruleMetadataUpdated?.categories[ruleCategory]
        ? ruleMetadataUpdated.categories[ruleCategory]
        : [];

    const translatedTriggerTypeOptions = triggerTypeOptions.map((opt: string) =>
        // Se è calendar e on event analyzed mostra on event loaded
        ruleCategory?.toLowerCase() === ruleCategoriesEnum.CALENDAR && opt === 'on_event_analyzed' ?
            ({ id: opt, value: intl.formatMessage({ id: opt.concat("_calendar"), defaultMessage: opt.concat("_calendar") }) })
            // Se è match e on event analyzed mostra match end
            : ruleCategory?.toLowerCase() === ruleCategoriesEnum.MATCH && opt === 'on_event_analyzed' ?
                ({ id: opt, value: intl.formatMessage({ id: opt.concat('_match'), defaultMessage: opt.concat('_match') }) })
                : //Fallback
                ({ id: opt, value: intl.formatMessage({ id: opt, defaultMessage: opt }) })
    );

    if (loading && mode === RULE_FORM_MODE.UPDATE) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="calc(100vh - 150px)">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box display={"flex"} flexDirection={"column"} gap={2}>

            {/* GRANDE CONTENITORE COMPATTO: Tutti gli elementi ereditano la stessa riga flessibile */}
            <Grid container spacing={2} sx={{ width: '100%' }}>

                {/* Automation Name: altezza standard, non forzata */}
                <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                        label="Automation Name"
                        variant="outlined"
                        value={ruleName}
                        onChange={(e) => setRuleName(e.target.value)}
                        fullWidth
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                </Grid>

                {/* Automation Description: 5 colonne su 12, più alta per via del multiline */}
                <Grid size={{ xs: 12, md: 5 }}>
                    <TextField
                        label="Automation Description"
                        multiline
                        rows={3}
                        fullWidth
                        variant="outlined"
                        value={ruleDescription}
                        onChange={(e) => setRuleDescription(e.target.value)}
                        sx={{
                            height: '100%',
                            '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start', borderRadius: '8px' },
                        }}
                    />
                </Grid>

                {/* Colonna Dropdown 1: Gestone tramite Stack */}
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <Stack spacing={2}>
                        <CustomAutoComplete
                            label="Automation Category"
                            options={translatedRuleCategoryOptions}
                            value={ruleCategory ? intl.formatMessage({ id: ruleCategory, defaultMessage: ruleCategory }) : null}
                            onChange={(_: any, newValue: any) => {
                                onRuleCategoryChange(newValue ? newValue.id : null);
                            }}
                            labelField={'value'}
                            valueField={'value'}
                            customAutocomplete={false}
                            multiple={false}
                            disabled={mode === RULE_FORM_MODE.UPDATE}
                        />
                        <CustomAutoComplete
                            disableClearable = {true}
                            label="Content Type"
                            options={translatedContentTypeOptions}
                            value={contentType ? intl.formatMessage({ id: contentType, defaultMessage: contentType }) : null}
                            onChange={(_: any, newValue: any) => {
                                onContentTypeChange(newValue ? newValue.id : null);
                            }}
                            valueField={"value"}
                            labelField={'value'}
                            customAutocomplete={false}
                            multiple={false}
                            disabled={mode === RULE_FORM_MODE.UPDATE}
                        />
                    </Stack>
                </Grid>

                {/* Colonna Dropdown 2 (Trigger + Delay originale) */}
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <Stack spacing={2}>
                        <CustomAutoComplete
                            label="Trigger Type"
                            options={translatedTriggerTypeOptions}
                            // Posso spiegare... praticamente se è calendar ed è on_event_analyzed allora mostra invece on_event_loaded e traducilo. Altrimenti null
                            value={rule ? ruleCategory === ruleCategoriesEnum.CALENDAR && rule ==='on_event_analyzed' ? intl.formatMessage({ id: rule.concat('_calendar'), defaultMessage: rule.concat('_calendar') }) : ruleCategory?.toLowerCase() === ruleCategoriesEnum.MATCH && rule === 'on_event_analyzed' ? ({id: rule, value: intl.formatMessage({ id: rule.concat('_match'), defaultMessage: rule.concat('_match') })}) :  ({id: rule, value: intl.formatMessage({ id: rule, defaultMessage: rule })}) : null}
                            // TODO: appena ci sono opzioni per periodic, bisogna eliminare questo filterOptions
                            filterOptions={(options: { id: string, value: string }[]) => {
                                return options.filter(option => option.id !== 'periodic');
                            }}
                            onChange={(_: any, newValue: any) => {
                                onRuleChange(newValue ? newValue.id : null);
                            }}
                            labelField={'value'}
                            valueField={'value'}
                            customAutocomplete={false}
                            multiple={false}
                            disabled={(!ruleMetadataUpdated?.categories[ruleCategory]) || searchParams.get("id") || (mode === RULE_FORM_MODE.UPDATE)}
                        />
                        {scheduler === 'trigger-based' && ruleCategory !== 'time' && schemaTriggers.length > 0 && (
                            <RuleConditionForm
                                schema={timeParams}
                                defaultValues={timeParamsValues}
                                gridSizes={{ xs: 12 }}
                                onChange={(key: string, value: any) => onConditionChangeTimeParams(key, value)}
                            />
                        )}
                    </Stack>
                </Grid>

            </Grid>

            {((scheduler === 'trigger-based' && ruleCategory !== 'time') || (rule === 'scheduled')) && schemaTriggers.length > 0 &&
                <Box>
                    {rule !== 'scheduled' && <Box>
                        <DividerLabel label={"Activation Conditions"} />
                        <RuleConditionForm
                            schema={schemaTriggers}
                            defaultValues={triggerValues}
                            onChange={(key: string, value: any) => onConditionChange(key, value)}
                        />
                        {(contentType === SUPPORTED_CONTENTS[CURRENT_SPORT as keyof typeof SUPPORTED_SPORTS].CLIP && !excludedArraysForSingleClip.includes(rule || '')) && ruleCategory === 'match' &&
                            <FormControlLabel control={
                                <Checkbox
                                    checked={isSingleClip}
                                    onChange={() => setIsSingleClip(value => !value)}
                                />
                            } label={"SINGLE CLIP"}/>
                        }
                    </Box>}
                    {!isSingleClip && <Box>
                        <DividerLabel label={"Filters"} />
                        <RuleConditionForm schema={schemaFilters} onChange={(key: string, value: any) => onConditionFilterChange(key, value)} defaultValues={filterValues}/>
                    </Box>}
                </Box>
            }
            {
                <Box sx={{ flexGrow: 1 }}>
                    {ruleMetadataUpdated?.time_params[rule] && contentType && timeParams && ruleCategory === 'time' &&
                        <Box>
                            <DividerLabel label={"Time Conditions"} />
                            <RuleConditionForm schema={timeParams} defaultValues={timeParamsValues} onChange={(key: string, value: any) => onConditionChangeTimeParams(key, value)} />
                        </Box>
                    }
                </Box>
            }
            {/*<div>Rule {rule}</div>
            <div>Metadata {JSON.stringify(ruleMetadata)}</div>
            <div>Metadata Triggers {JSON.stringify(ruleMetadata && ruleMetadata.triggers)}</div>
            <div>Rule Trigger {JSON.stringify(ruleMetadata && rule && ruleMetadata.triggers[rule])}</div>
            <div>Rule Trigger action {JSON.stringify(ruleMetadata && rule && ruleMetadata.triggers[rule].actions)}</div>*/}
            {contentType &&
                <ContentAggregation
                    usageMode={AGGREGATION_USAGE_TYPE.PRESET}
                    action={action}
                    onActionChange={setAction}
                    actions={rule && ruleMetadataUpdated && ruleMetadataUpdated?.triggers[rule] && ruleMetadataUpdated?.triggers[rule].actions ? ruleMetadataUpdated?.triggers[rule].actions : []}
                />
            }
            {
                contentType && rule && action && <Typography>Action Parameters</Typography>
            }
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems="flex-start"
            >
                {
                    <Box onBlur={validateClipNamePlaceholders}>
                        {contentType && rule && action && renderActionContent(action, clipName, setClipName)}
                    </Box>
                }
            </Stack>
            {action && action === TRIGGER_ACTION.CREATE_MATERIALIZE_PUBLISH_LOGIC_CLIP &&
                <SocialPublish
                    logicClip={null}
                    usageType={AGGREGATION_USAGE_TYPE.PRESET}
                    onPresetPublish={(test: any) => {
                        if (searchParams.get("id") || mode === RULE_FORM_MODE.UPDATE) {
                            updateRule({
                                id: ruleId,
                                name: ruleName,
                                description: ruleDescription,
                                activation_filters: formatConditionsForQueries(triggerValues),
                                action: action,
                                parameters: {
                                    highlight_type: contentType,
                                    suggest: isSingleClip ? null : {
                                        min_duration: contentSpecificParams?.minDuration,
                                        max_duration: contentSpecificParams?.maxDuration,
                                        highlight_type: contentType
                                    },
                                    create: {
                                        name: clipName,
                                        creation_settings: creationSettingsCreator()
                                    },
                                    publish: {
                                        publish: {...test, LLMGeneration: true},
                                    },
                                    publish_delay: timeParamsValues['delay']
                                },
                                time_params: {},
                                parameters_filters: isSingleClip ? undefined : formatParametersFilters(filterValues)
                            }).then(() => enqueueSnackbar('Rule updated')).catch(() => enqueueSnackbar('Rule not updated'))

                        } else {

                            createRule({
                                name: ruleName,
                                description: ruleDescription,
                                enabled: true,
                                activation_filters: formatConditionsForQueries(triggerValues),
                                scheduler: ruleMetadataUpdated?.categories['time'].includes(rule) ? 'time-based' : 'trigger-based',
                                trigger: rule,
                                action: action,
                                parameters: {
                                    highlight_type: contentType,
                                    suggest: isSingleClip ? null : {
                                        min_duration: contentSpecificParams?.minDuration,
                                        max_duration: contentSpecificParams?.maxDuration,
                                        highlight_type: contentType
                                    },
                                    create: {
                                        name: clipName,
                                        creation_settings: creationSettingsCreator()
                                    },
                                    publish: {
                                        publish: test,
                                    },
                                    publish_delay: timeParamsValues['delay']
                                },
                                time_params: {},
                                parameters_filters: isSingleClip ? undefined : formatParametersFilters(filterValues)
                            })
                                .then(r => {
                                    enqueueSnackbar('Rule created')
                                    setRuleId(r.id);
                                    setMode(RULE_FORM_MODE.UPDATE);
                                    handleAddId(String(r.id));
                                })
                                .catch((error) => {
                                    enqueueSnackbar('Impossible to create the rule')
                                    console.log("Impossibile creare la rule", error);
                                });
                        }
                    }}
                />
            }
            {
                contentType && rule && action &&
                <Box>
                    <Stack spacing={2} direction={'row'} sx={{justifyContent: 'flex-end'}}>
                        <Button variant="contained" onClick={() => setAction(null)}>Cancel</Button>
                        {action && action !== TRIGGER_ACTION.CREATE_MATERIALIZE_PUBLISH_LOGIC_CLIP &&
                            <Button variant="contained" onClick={() => (searchParams.get("id") || (mode === RULE_FORM_MODE.UPDATE)) ? (
                                    // UPDATE
                                    !errorPlaceholder ? (
                                            // NON C'È ERRORE NEL PLACEHOLDER
                                            updateRule({
                                                id: ruleId,
                                                name: ruleName,
                                                description: ruleDescription,
                                                activation_filters: formatConditionsForQueries(triggerValues),
                                                action: action,
                                                parameters: {
                                                    highlight_type: contentType,
                                                    suggest: isSingleClip ? null : {
                                                        min_duration: contentSpecificParams?.minDuration,
                                                        max_duration: contentSpecificParams?.maxDuration,
                                                        highlight_type: contentType
                                                    },
                                                    create: {
                                                        name: clipName,
                                                        creation_settings: creationSettingsCreator()
                                                    }
                                                },
                                                time_params: timeParamsValues,
                                                parameters_filters: isSingleClip ? undefined : formatParametersFilters(filterValues)
                                            }).then(() => enqueueSnackbar('Rule updated')).catch(() => enqueueSnackbar('Impossible to update the rule'))) :
                                        // C'È ERRORE NEL PLACEHOLDER
                                        enqueueSnackbar('Impossible to update a clip with invalid name')) :

                                //CREATE RULE
                                (
                                    // NESSUN ERRORE NEL PLACEHOLDER
                                    !errorPlaceholder ?
                                        (
                                            createRule(
                                                {
                                                    name: ruleName,
                                                    description: ruleDescription,
                                                    enabled: true,
                                                    activation_filters: formatConditionsForQueries(triggerValues),
                                                    scheduler: ruleMetadataUpdated?.categories['time'].includes(rule) ? 'time-based' : 'trigger-based',
                                                    trigger: rule,
                                                    action: action,
                                                    parameters: {
                                                        highlight_type: contentType,
                                                        suggest: isSingleClip ? null : {
                                                            min_duration: contentSpecificParams?.minDuration,
                                                            max_duration: contentSpecificParams?.maxDuration,
                                                            highlight_type: contentType
                                                        },
                                                        create: {
                                                            name: clipName,
                                                            creation_settings: creationSettingsCreator()
                                                        }
                                                    },
                                                    time_params: timeParamsValues,
                                                    parameters_filters: isSingleClip ? undefined : formatParametersFilters(filterValues)
                                                }).then(r => {
                                                setRuleId(r.id), setMode(RULE_FORM_MODE.UPDATE), handleAddId(String(r.id)), enqueueSnackbar('Rule created')
                                            }).catch(() => {
                                                console.log("Impossibile creare la rule"), enqueueSnackbar('Impossible to create the rule')
                                            })
                                        ) :
                                        // PROBLEMA CON CLIP NAME
                                        enqueueSnackbar('Impossible to create a clip with invalid name')
                                )}>Execute</Button>}</Stack>
                </Box>
            }
        </Box>
    )

}

export default RuleForgePage;